/**
 * Cancel One-Time Job Modal
 * Two-step confirmation for one-time job cancellation to prevent accidental cancellations
 */

import { useState } from 'react';
import type { OneTimeJob } from '../../../../../shared/types';
import { formatCurrency, formatDateRange } from '../../../../../shared/utils/formatting';
import { BaseModal } from '../../common/BaseModal';
import { ModalButtonGroup } from '../../common/ModalButtonGroup';
import { ERROR_MESSAGE_STYLE } from '../../../styles/modalStyles';

interface CancelOneTimeJobModalProps {
  oneTimeJob: OneTimeJob;
  onClose: () => void;
  onCancel: (oneTimeJobId: string) => Promise<void>;
  assignedManagerName?: string;
  assignedStaffName?: string;
  companyTimezone?: string;
}

export function CancelOneTimeJobModal({
  oneTimeJob,
  onClose,
  onCancel,
  assignedManagerName,
  assignedStaffName,
  companyTimezone,
}: CancelOneTimeJobModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCancel = async () => {
    try {
      setLoading(true);
      setError('');
      await onCancel(oneTimeJob.id);
      onClose();
    } catch (err) {
      console.error('Error cancelling one-time job:', err);
      setError('Failed to cancel one-time job. Please try again.');
      setLoading(false);
    }
  };

  // Build assignment info text
  const getAssignmentInfo = () => {
    if (assignedStaffName) {
      return `Assigned to: ${assignedStaffName} (Staff)`;
    }
    if (assignedManagerName) {
      return `Assigned to: ${assignedManagerName} (Manager)`;
    }
    return 'Not yet assigned';
  };

  return (
    <BaseModal
      isOpen={true}
      onClose={onClose}
      title=""
      size="medium"
      closeOnEscape={!loading}
      closeOnBackdropClick={!loading}
      loading={loading}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <div style={{ fontSize: '32px' }}>⚠️</div>
        <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#dc2626', margin: 0 }}>
          Cancel One-Time Job
        </h2>
      </div>

      <div style={{ marginBottom: '24px', color: '#6b7280', fontSize: '15px', lineHeight: '1.6' }}>
        <p style={{ marginBottom: '12px', color: '#111827', fontWeight: '600' }}>
          Are you sure you want to cancel this one-time job?
        </p>

        {/* One-Time Job Details */}
        <div
          style={{
            backgroundColor: '#f9fafb',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px',
          }}
        >
          <div style={{ marginBottom: '8px' }}>
            <strong style={{ color: '#111827' }}>{oneTimeJob.title}</strong>
          </div>
          {oneTimeJob.description && (
            <div style={{ fontSize: '14px', marginBottom: '8px', color: '#6b7280' }}>
              {oneTimeJob.description}
            </div>
          )}
          <div style={{ fontSize: '14px', color: '#6b7280', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              <span>{oneTimeJob.location.address}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              <span>{formatDateRange(oneTimeJob.scheduledStart, oneTimeJob.scheduledEnd, "en-IE", companyTimezone)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
              <span>{formatCurrency(oneTimeJob.hourlyRate)}</span>
            </div>
            <div style={{ marginTop: '4px', fontWeight: '600', color: '#111827' }}>
              {getAssignmentInfo()}
            </div>
          </div>
        </div>

        <p style={{ marginBottom: '12px' }}>
          Cancelling this one-time job will:
        </p>
        <ul style={{ paddingLeft: '24px', marginBottom: '12px', color: '#374151' }}>
          <li>Set the one-time job status to <strong>cancelled</strong></li>
          {(assignedManagerName || assignedStaffName) && (
            <li>Send a cancellation notification email to the assigned {assignedStaffName ? 'staff member' : 'manager'}</li>
          )}
          <li>Remove this one-time job from the assigned user's active job list</li>
          <li>Prevent any time entries from being created for this one-time job</li>
        </ul>

        <div
          style={{
            padding: '16px',
            backgroundColor: '#fef3c7',
            border: '1px solid #fbbf24',
            borderRadius: '8px',
            marginTop: '16px',
          }}
        >
          <p style={{ color: '#92400e', fontWeight: '600', fontSize: '14px', margin: 0 }}>
            Note: This action cannot be undone. Cancelled one-time jobs remain in the system for record-keeping but cannot be restored.
          </p>
        </div>
      </div>

      {error && (
        <div style={ERROR_MESSAGE_STYLE}>
          {error}
        </div>
      )}

      <ModalButtonGroup
        onCancel={onClose}
        onSubmit={handleCancel}
        submitLabel="Cancel One-Time Job"
        cancelLabel="Keep One-Time Job"
        submitVariant="danger"
        isLoading={loading}
        loadingLabel="Cancelling..."
      />
    </BaseModal>
  );
}
